import {
  Button,
  Card,
  Dropdown,
  DropdownItem,
  Label,
  Separator,
  Textarea,
} from 'platform/components';
import {Box, HStack, Show, Space} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {always} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import featureFlags from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {AllOrNone, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {PredefinedNotes} from '../PredefinedNotes/PredefinedNotes';

export type TemplateBoxTemplate = {id: string; title: string; [key: string]: unknown};

type DocumentTemplateBoxProps = {
  title: string;
  type?: string | Nullish;
  isSelected?: boolean;
  selectedTemplate?: TemplateBoxTemplate | Nullish;
  templates?: TemplateBoxTemplate[] | Nullish;
  authorizationTemplates?: TemplateBoxTemplate[] | Nullish;
  authorizationProfileLabel?: string | Nullish;
  onSelect: (isSelected: boolean) => void;
  onTemplateChange: (document: TemplateBoxTemplate) => void;
  isDisabled?: boolean;
} & AllOrNone<{
  isWithNote: boolean;
  onNoteChange: (note: string | null) => void;
  noteLabel?: string;
}> &
  TestIdProps;

export function DocumentTemplateBox(props: DocumentTemplateBoxProps) {
  const [note, setNote] = useState<string | null>(null);

  if (isNilOrEmpty(props.templates) && isNilOrEmpty(props.authorizationTemplates)) {
    return null;
  }

  const noteType = match(props.type)
    .with('purchase-brokerage-contract', () => 'brokerage-contract')
    .with('sale-brokerage-contract', () => 'brokerage-contract')
    .otherwise(always(props.type));

  const handleNoteChange = (note: string | null) => {
    setNote(note);
    props.onNoteChange?.(note);
  };

  return (
    <Card
      title={props.title}
      variant="inlineGrey"
      isExpanded={props.isWithNote}
      control={{
        type: 'checkbox',
        value: Boolean(props.isSelected),
        onChange: props.onSelect,
        isDisabled: props.isDisabled,
        'data-testid': suffixTestId('isSelected', props),
      }}
      actions={[
        {
          type: 'custom',
          component: (
            <Dropdown
              data-testid={props['data-testid']}
              isHeightLimited
              dropdownControl={
                <Button
                  variant="secondary"
                  rightIcon="navigation/expand_more"
                  title={props.selectedTemplate?.title || i18n.t('general.labels.template')}
                />
              }
            >
              <Show
                when={isNotNilOrEmpty(props.authorizationTemplates)}
                whenFeatureEnabled={featureFlags.AUTH_PROFILES_TEMPLATES}
              >
                <Label>
                  {props.authorizationProfileLabel ?? i18n.t('general.labels.template')}
                </Label>
                <Separator spacing={0} />
                {props.authorizationTemplates?.map((template, index) => (
                  <DropdownItem
                    data-testid={suffixTestId(`auth-profile-template[${index}]`, props)}
                    key={template.id}
                    label={template.title}
                    onClick={() => props.onTemplateChange(template)}
                  />
                ))}

                <Space vertical={2} />
                <Label>{i18n.t('entity.authorizationProfiles.labels.withoutAuthorization')}</Label>
                <Separator spacing={0} />
              </Show>

              <Show when={isNotNilOrEmpty(props.templates)}>
                {props.templates?.map((template, index) => (
                  <DropdownItem
                    data-testid={suffixTestId(`template[${index}]`, props)}
                    key={template.id}
                    label={template.title}
                    onClick={() => props.onTemplateChange(template)}
                  />
                ))}
              </Show>
            </Dropdown>
          ),
        },
      ]}
      data-testid={props['data-testid']}
    >
      {props.isWithNote && (
        <>
          <HStack align="flex-end">
            <Box flex={1}>
              <Label>{props.noteLabel || i18n.t('general.labels.note')}</Label>
            </Box>
            {noteType && (
              <PredefinedNotes
                note={note}
                onPrefill={handleNoteChange}
                context={noteType}
                resource="BUSINESS_CASE"
                isLinkVariant
                data-testid={suffixTestId('predefinedNotes', props)}
              />
            )}
          </HStack>
          <Textarea
            name="note"
            onChange={handleNoteChange}
            value={note}
            minRows={2}
            data-testid={suffixTestId('note', props)}
          />
        </>
      )}
    </Card>
  );
}
